/**
 * Event page — divisions list + entry into the registration flow.
 *
 * Coach taps a division → division stashed in FlowContext, then we
 * route to /auth (or skip auth if a JWT is already cached for this
 * embed in sessionStorage).
 *
 * Single-division events skip this step entirely: there's nothing to
 * choose, so we auto-select the lone division and advance.
 *
 * Grouped divisions (Boys / Girls, Softball / Baseball) show as tabs, one
 * group at a time. Who's playing is per division: a row with teams carries
 * a "See teams" link that opens that division's list in place (header Back
 * returns), with "Register for <division>" when it's open. The row itself
 * still picks the division, so registering stays one tap. No team counts:
 * most organizers don't cap divisions.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import type { EmbedClient, DivisionResponse, RegisteredTeam } from '@/api/embed';
import { useBackOverride } from '@/state/backOverride';
import { useFlow } from '@/state/FlowContext';
import { useFlowNavigate } from '@/state/useFlowNavigate';
import { useVocab } from '@/state/vocab';
import { groupDivisions } from './divisionGroups';
import './EventPage.css';

interface EventPageProps {
  client: EmbedClient;
}

export function EventPage({ client }: EventPageProps) {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useFlowNavigate();
  const { selectDivision } = useFlow();
  const vocab = useVocab();
  const [divisions, setDivisions] = useState<DivisionResponse[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Registered teams, for each division's count and "See teams" list.
  // Loaded alongside divisions; failures are swallowed (it's supplementary,
  // never blocks registering).
  const [teams, setTeams] = useState<RegisteredTeam[] | null>(null);
  const autoPickedRef = useRef(false);
  // The division whose teams are open ("See teams"), or null for the list.
  const [viewing, setViewing] = useState<DivisionResponse | null>(null);
  const closeTeams = useCallback(() => setViewing(null), []);
  useBackOverride(viewing ? closeTeams : null);
  // Switching between the list and a division's teams starts at the top;
  // arriving on the screen doesn't move anything.
  const switched = useRef(false);
  useEffect(() => {
    if (!switched.current) { switched.current = true; return; }
    window.scrollTo?.(0, 0);
  }, [viewing]);
  const groups = useMemo(() => groupDivisions(divisions ?? []), [divisions]);
  const [tab, setTab] = useState<string | null>(null);

  // Teams by the division they entered. Teams without a listed division
  // aren't shown here.
  const byDivision = useMemo(() => {
    const map = new Map<string, RegisteredTeam[]>();
    for (const t of teams ?? []) {
      if (t.division) map.set(t.division, [...(map.get(t.division) ?? []), t]);
    }
    return map;
  }, [teams]);

  useEffect(() => {
    if (!eventId) return;
    client
      .getDivisions(eventId)
      .then(setDivisions)
      .catch((err) => {
        setError(
          err?.response?.status === 404
            ? 'This event is not available through this registration link.'
            : 'Could not load divisions. Please try again.',
        );
      });
  }, [client, eventId]);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    client
      .getRegisteredTeams(eventId)
      .then((res) => { if (!cancelled) setTeams(res.teams); })
      .catch(() => { if (!cancelled) setTeams([]); });
    return () => { cancelled = true; };
  }, [client, eventId]);

  const onPickDivision = useCallback(
    (name: string, replace = false) => {
      selectDivision(name);
      const dest = client.isAuthed()
        ? `/events/${eventId}/teams`
        : `/events/${eventId}/auth`;
      navigate(dest, replace ? { replace: true } : undefined);
    },
    [client, eventId, navigate, selectDivision],
  );

  // Single-division events have nothing to choose — auto-select the lone
  // division and advance. `replace: true` keeps this vestigial step out
  // of history so Back returns to the product chooser instead of bouncing
  // forward through the skip again. Ref-guarded so it fires exactly once
  // even under StrictMode's double-invoked effects. Full/closed divisions
  // don't auto-advance — the list renders so the coach sees why.
  useEffect(() => {
    if (!divisions || divisions.length !== 1 || autoPickedRef.current) return;
    if (divisions[0].is_full || divisions[0].is_closed) return;
    autoPickedRef.current = true;
    onPickDivision(divisions[0].name, true);
  }, [divisions, onPickDivision]);

  if (error) {
    return <div role="alert" className="tn-event__error">{error}</div>;
  }

  if (!divisions) {
    return <div className="tn-event__loading">Loading divisions…</div>;
  }

  // Auto-skip in flight for a single-division event — render the loading
  // placeholder rather than flash a one-item list for a frame. A full or
  // closed lone division doesn't auto-skip; fall through to the list so
  // the coach sees the state instead of an infinite spinner.
  if (divisions.length === 1 && !divisions[0].is_full && !divisions[0].is_closed) {
    return <div className="tn-event__loading">Loading…</div>;
  }

  if (divisions.length === 0) {
    return (
      <div className="tn-card tn-event__empty">
        <h2>No {vocab.divisionPlural} yet</h2>
        <p>
          The organizer hasn't published {vocab.divisionPlural} for this event yet.
          Check back soon.
        </p>
      </div>
    );
  }

  if (viewing) {
    const list = byDivision.get(viewing.name) ?? [];
    const open = !viewing.is_full && !viewing.is_closed;
    const state = viewing.is_full ? 'Full' : viewing.is_closed ? 'Registration closed' : null;
    return (
      <div className="tn-event tn-event--teams">
        <h1 className="tn-event__title">{viewing.name}</h1>
        {state ? <p className="tn-event__teams-state">{state}</p> : null}
        <TeamList teams={list} />
        <div className="tn-event__teams-actions">
          {open ? (
            <button type="button" className="tn-btn tn-btn--primary" onClick={() => onPickDivision(viewing.name)}>
              Register for {viewing.name}
            </button>
          ) : (
            <button type="button" className="tn-btn tn-btn--secondary" onClick={closeTeams}>
              Back to {vocab.divisionPlural}
            </button>
          )}
        </div>
      </div>
    );
  }

  // One tab per group when the names group; the first until one is picked.
  const tabbed = groups.length > 1;
  const shown = groups.find((g) => g.label === tab) ?? groups[0];

  return (
    <div className="tn-event">
      <h1 className="tn-event__title">Choose your {vocab.division}</h1>
      {tabbed ? (
        <div className="tn-event__tabs" role="tablist" aria-label={vocab.divisionPlural}>
          {groups.map((g) => (
            <button
              key={g.label ?? 'other'}
              type="button"
              role="tab"
              aria-selected={g === shown}
              className={`tn-event__tab${g === shown ? ' tn-event__tab--on' : ''}`}
              onClick={() => setTab(g.label)}
            >
              {g.label}
            </button>
          ))}
        </div>
      ) : null}
      {(tabbed ? [shown] : groups).map((group) => (
      <section key={group.label ?? 'all'} className="tn-event__group" aria-label={group.label ?? undefined}>
      {group.label && !tabbed ? <h2 className="tn-event__group-label">{group.label}</h2> : null}
      <ul className="tn-event__divisions">
        {group.items.map(({ division: d, display }) => {
          const unavailable = d.is_full || d.is_closed;
          const hasTeams = (byDivision.get(d.name)?.length ?? 0) > 0;
          return (
            <li key={d.id} className={`tn-event__division-item${hasTeams ? ' tn-event__division-item--who' : ''}`}>
              <button
                type="button"
                className={`tn-event__division${unavailable ? ' tn-event__division--unavailable' : ''}`}
                disabled={unavailable}
                onClick={() => onPickDivision(d.name)}
              >
                <span className="tn-event__division-name">{display}</span>
                {unavailable ? (
                  <span className="tn-event__division-unavailable">
                    {d.is_full ? 'Full' : 'Registration closed'}
                  </span>
                ) : null}
              </button>
              {hasTeams ? (
                <button
                  type="button"
                  className="tn-event__division-who"
                  onClick={() => setViewing(d)}
                  aria-label={`See ${vocab.entrantPlural} in ${d.name}`}
                >
                  See {vocab.entrantPlural}
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>
      </section>
      ))}
    </div>
  );
}

function TeamList({ teams }: { teams: RegisteredTeam[] }) {
  const place = (t: RegisteredTeam) => [t.city, t.state].filter(Boolean).join(', ');
  return (
    <ul className="tn-event__teams">
      {teams.map((t) => (
        <li key={t.id} className="tn-event__team">
          {t.logo_url ? (
            <img className="tn-event__team-logo" src={t.logo_url} alt="" loading="lazy" />
          ) : (
            <span className="tn-event__team-logo tn-event__team-logo--placeholder" aria-hidden="true">
              {t.name.slice(0, 1).toUpperCase()}
            </span>
          )}
          <span className="tn-event__team-info">
            <span className="tn-event__team-name">{t.name}</span>
            {place(t) ? <span className="tn-event__team-meta">{place(t)}</span> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}
